import { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  XMarkIcon,
  Squares2X2Icon,
  ListBulletIcon,
  MapPinIcon,
  AdjustmentsHorizontalIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import { RootState } from '@/store';
import { fetchProducts, fetchCategories } from '@/store/slices/productsSlice';
import {
  filterProducts,
  setFilters,
  clearAllFilters,
  setPage,
  setSkip,
} from '@/store/slices/productFiltersSlice';
import { productsApi } from '@/services/api';
import { Product } from '@/types';
import { ROUTES } from '@/utils/constants';
import { cn } from '@/utils/cn';
import { ProductCarousel } from '@/components/product/ProductCarousel';
import { Breadcrumbs } from '@/components/ui/Breadcrumbs';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Pagination } from '@/components/ui/Pagination';
import { SkeletonProductGrid } from '@/components/ui/Skeleton';
import ProductSidebar from '@/components/layout/ProductSidebar';
import LocationSelectionModal from '@/components/location/LocationSelectionModal';
import ProductCard from '@/components/ui/ProductCard';
import { getPrimaryImage } from '@/utils/imageUtils';
import { fadeInUp } from '@/utils/animations';
import CategorySidebar, { CategorySidebarMobile } from '@/components/navigation/CategorySidebar';
import { fetchCategoryTree } from '@/store/slices/categoriesSlice';
import { Bars3Icon } from '@heroicons/react/24/outline';
import { AppDispatch } from '@/store';

const ProductsPageSimple = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { products, categories, isLoading } = useSelector((state: RootState) => state.products);
  const [showCategoryNav, setShowCategoryNav] = useState(false);
  const {
    filteredProducts,
    isLoading: isFilterLoading,
    total,
    page,
    pages,
    limit,
    skip,
    product_types,
    category_ids,
    search_query,
    locations: selectedLocations,
  } = useSelector((state: RootState) => state.productFilters);

  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [lastUrlParams, setLastUrlParams] = useState<string>('');
  const [localSearchQuery, setLocalSearchQuery] = useState(search_query || '');
  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);
  const [sortBy, setSortBy] = useState<string>('relevance');
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [showBrandsDropdown, setShowBrandsDropdown] = useState(false);
  const [showConditionDropdown, setShowConditionDropdown] = useState(false);
  const [showPriceDropdown, setShowPriceDropdown] = useState(false);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setShowBrandsDropdown(false);
      setShowConditionDropdown(false);
      setShowPriceDropdown(false);
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Breadcrumb items
  const breadcrumbItems = [
    { label: 'Home', path: ROUTES.HOME },
    { label: 'Products', path: ROUTES.PRODUCTS },
  ];

  // Debounced search
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      dispatch(
        setFilters({
          category_ids,
          product_types,
          brands: [],
          conditions: [],
          locations: selectedLocations,
          min_price: undefined,
          max_price: undefined,
          search_query: localSearchQuery,
        })
      );
      setTimeout(() => dispatch(filterProducts() as any), 100);
    }, 300);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [localSearchQuery, category_ids, product_types, selectedLocations, dispatch]);

  // Handle URL query parameters
  useEffect(() => {
    const currentUrlParams = searchParams.toString();

    if (currentUrlParams === lastUrlParams) {
      return;
    }

    const productTypeParam = searchParams.get('product_type') || searchParams.get('type');
    const categoryParam = searchParams.get('category');
    const searchParam = searchParams.get('search');

    dispatch(clearAllFilters());

    setTimeout(() => {
      if (searchParam) {
        // Handle search query from URL
        setLocalSearchQuery(searchParam);
        dispatch(
          setFilters({
            category_ids: [],
            product_types: [],
            brands: [],
            conditions: [],
            locations: [],
            min_price: undefined,
            max_price: undefined,
            search_query: searchParam,
          })
        );

        setTimeout(() => {
          dispatch(filterProducts() as any);
        }, 300);
      } else if (productTypeParam) {
        dispatch(
          setFilters({
            category_ids: [],
            product_types:
              productTypeParam === 'recycled'
                ? ['recycled']
                : productTypeParam === 'brand_new'
                ? ['brand_new']
                : [],
            brands: [],
            conditions: [],
            locations: [],
            min_price: undefined,
            max_price: undefined,
            search_query: '',
          })
        );

        setTimeout(() => {
          dispatch(filterProducts() as any);
        }, 300);
      } else if (categoryParam) {
        const categoryId = parseInt(categoryParam);
        if (!isNaN(categoryId)) {
          dispatch(
            setFilters({
              category_ids: [categoryId],
              product_types: [],
              brands: [],
              conditions: [],
              locations: [],
              min_price: undefined,
              max_price: undefined,
              search_query: '',
            })
          );

          setTimeout(() => {
            dispatch(filterProducts() as any);
          }, 300);
        }
      } else {
        dispatch(fetchProducts({ skip: 0, limit: 20 }));
      }

      setLastUrlParams(currentUrlParams);
    }, 300);
  }, [searchParams, dispatch]);

  // Initial load
  useEffect(() => {
    const productTypeParam = searchParams.get('product_type') || searchParams.get('type');
    const categoryParam = searchParams.get('category');
    const searchParam = searchParams.get('search');

    dispatch(fetchCategories());
    dispatch(fetchCategoryTree()); // Load category tree for sidebar

    if (!productTypeParam && !categoryParam && !searchParam && lastUrlParams === '') {
      dispatch(fetchProducts({ skip: skip, limit: limit }));
    }
  }, [dispatch, skip, limit]);

  const handleLocationModalSelect = (locations: string[]) => {
    dispatch(setFilters({ locations }));
    setTimeout(() => dispatch(filterProducts() as any), 100);
  };

  const handlePageChange = (newPage: number) => {
    const newSkip = (newPage - 1) * limit;
    dispatch(setSkip(newSkip));
    dispatch(setPage(newPage));

    if (hasActiveFilters) {
      setTimeout(() => dispatch(filterProducts() as any), 100);
    } else {
      dispatch(fetchProducts({ skip: newSkip, limit: limit }));
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleClearFilters = () => {
    dispatch(clearAllFilters());
    setLocalSearchQuery('');
    navigate(ROUTES.PRODUCTS);
  };

  const productTypeParam = searchParams.get('product_type') || searchParams.get('type');
  const categoryParam = searchParams.get('category');
  const searchParam = searchParams.get('search');
  const hasUrlFilters = !!(productTypeParam || categoryParam || searchParam);

  const currentFilters = useSelector((state: RootState) => state.productFilters);
  const hasActiveFilters =
    currentFilters.category_ids.length > 0 ||
    currentFilters.product_types.length > 0 ||
    currentFilters.brands.length > 0 ||
    currentFilters.conditions.length > 0 ||
    currentFilters.locations.length > 0 ||
    currentFilters.min_price !== undefined ||
    currentFilters.max_price !== undefined ||
    currentFilters.search_query.trim() !== '';

  const displayProducts = hasActiveFilters || hasUrlFilters ? filteredProducts : products;
  const isLoadingProducts = isLoading || isFilterLoading;

  const totalPages =
    hasActiveFilters || hasUrlFilters
      ? pages > 0
        ? pages
        : Math.ceil((total || filteredProducts.length) / limit)
      : Math.ceil((products.length || 20) / limit);
  const currentPage =
    hasActiveFilters || hasUrlFilters ? (page > 0 ? page : 1) : Math.floor(skip / limit) + 1;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-900">
      {/* Mobile Category Navigation */}
      <CategorySidebarMobile
        isOpen={showCategoryNav}
        onClose={() => setShowCategoryNav(false)}
      />

      {/* Breadcrumbs */}
      <div className="bg-white dark:bg-dark-850 border-b border-gray-200 dark:border-dark-700">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Breadcrumbs items={breadcrumbItems} />
        </div>
      </div>

      <div className="flex">
        {/* Category Sidebar - Desktop */}
        <aside className="hidden lg:block w-72 flex-shrink-0 bg-white dark:bg-dark-850 border-r border-gray-200 dark:border-dark-700">
          <div className="sticky top-0 h-screen overflow-y-auto">
            <CategorySidebar collapsible={false} className="border-0 rounded-none h-full" />
          </div>
        </aside>

        {/* Main Content */}
        <div className="flex-1 min-w-0 px-4 sm:px-6 lg:px-8 py-6">
            {/* Top Filter Bar */}
            <div className="bg-white dark:bg-dark-800 rounded-xl border border-gray-200 dark:border-dark-700 p-4 mb-6">
              <div className="flex flex-wrap items-center gap-3">
                {/* Location */}
                <Button
                  variant="outline"
                  size="md"
                  onClick={() => setIsLocationModalOpen(true)}
                  leftIcon={<MapPinIcon className="h-5 w-5" />}
                >
                  {selectedLocations.length > 0
                    ? `${selectedLocations.length} locations`
                    : 'Location'}
                </Button>

                {/* Brands Filter Dropdown */}
                <div className="relative" onClick={(e) => e.stopPropagation()}>
                  <Button
                    variant="outline"
                    size="md"
                    onClick={() => { setShowBrandsDropdown(!showBrandsDropdown); setShowConditionDropdown(false); setShowPriceDropdown(false); }}
                    rightIcon={<ChevronDownIcon className={cn("h-4 w-4 transition-transform", showBrandsDropdown && "rotate-180")} />}
                  >
                    Brands {currentFilters.brands.length > 0 && `(${currentFilters.brands.length})`}
                  </Button>
                  <AnimatePresence>
                    {showBrandsDropdown && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="absolute top-full left-0 mt-2 w-64 bg-white dark:bg-dark-800 rounded-lg shadow-lg border border-gray-200 dark:border-dark-700 z-50 max-h-64 overflow-y-auto"
                      >
                        <div className="p-3 space-y-2">
                          {categories.flatMap(cat =>
                            products.filter(p => p.category_id === cat.id).map(p => p.brand)
                          ).filter((brand, index, self) => brand && self.indexOf(brand) === index).slice(0, 20).map((brand) => (
                            <label key={brand} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-dark-700 p-2 rounded">
                              <input
                                type="checkbox"
                                checked={currentFilters.brands.includes(brand!)}
                                onChange={(e) => {
                                  const newBrands = e.target.checked
                                    ? [...currentFilters.brands, brand!]
                                    : currentFilters.brands.filter(b => b !== brand);
                                  dispatch(setFilters({ brands: newBrands }));
                                  setTimeout(() => dispatch(filterProducts() as any), 100);
                                }}
                                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                              />
                              <span className="text-sm text-gray-700 dark:text-gray-300">{brand}</span>
                            </label>
                          ))}
                          {products.filter(p => p.brand).length === 0 && (
                            <p className="text-sm text-gray-500 p-2">No brands available</p>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Condition Filter Dropdown */}
                <div className="relative" onClick={(e) => e.stopPropagation()}>
                  <Button
                    variant="outline"
                    size="md"
                    onClick={() => { setShowConditionDropdown(!showConditionDropdown); setShowBrandsDropdown(false); setShowPriceDropdown(false); }}
                    rightIcon={<ChevronDownIcon className={cn("h-4 w-4 transition-transform", showConditionDropdown && "rotate-180")} />}
                  >
                    Condition {product_types.length > 0 && `(${product_types.length})`}
                  </Button>
                  <AnimatePresence>
                    {showConditionDropdown && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="absolute top-full left-0 mt-2 w-48 bg-white dark:bg-dark-800 rounded-lg shadow-lg border border-gray-200 dark:border-dark-700 z-50"
                      >
                        <div className="p-3 space-y-2">
                          <label className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-dark-700 p-2 rounded">
                            <input
                              type="checkbox"
                              checked={product_types.includes('brand_new')}
                              onChange={(e) => {
                                const newTypes = e.target.checked
                                  ? [...product_types, 'brand_new']
                                  : product_types.filter(t => t !== 'brand_new');
                                dispatch(setFilters({ product_types: newTypes }));
                                setTimeout(() => dispatch(filterProducts() as any), 100);
                              }}
                              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                            />
                            <span className="text-sm text-gray-700 dark:text-gray-300">Brand New</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-dark-700 p-2 rounded">
                            <input
                              type="checkbox"
                              checked={product_types.includes('recycled')}
                              onChange={(e) => {
                                const newTypes = e.target.checked
                                  ? [...product_types, 'recycled']
                                  : product_types.filter(t => t !== 'recycled');
                                dispatch(setFilters({ product_types: newTypes }));
                                setTimeout(() => dispatch(filterProducts() as any), 100);
                              }}
                              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                            />
                            <span className="text-sm text-gray-700 dark:text-gray-300">Recycled</span>
                          </label>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Price Range Filter Dropdown */}
                <div className="relative" onClick={(e) => e.stopPropagation()}>
                  <Button
                    variant="outline"
                    size="md"
                    onClick={() => { setShowPriceDropdown(!showPriceDropdown); setShowBrandsDropdown(false); setShowConditionDropdown(false); }}
                    rightIcon={<ChevronDownIcon className={cn("h-4 w-4 transition-transform", showPriceDropdown && "rotate-180")} />}
                  >
                    Price {(currentFilters.min_price || currentFilters.max_price) && '(Set)'}
                  </Button>
                  <AnimatePresence>
                    {showPriceDropdown && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="absolute top-full left-0 mt-2 w-64 bg-white dark:bg-dark-800 rounded-lg shadow-lg border border-gray-200 dark:border-dark-700 z-50"
                      >
                        <div className="p-4 space-y-4">
                          <div className="flex items-center gap-2">
                            <input
                              type="number"
                              placeholder="Min"
                              value={currentFilters.min_price || ''}
                              onChange={(e) => {
                                dispatch(setFilters({ min_price: e.target.value ? Number(e.target.value) : undefined }));
                                setTimeout(() => dispatch(filterProducts() as any), 300);
                              }}
                              className="w-full px-3 py-2 border border-gray-300 dark:border-dark-600 rounded-lg bg-white dark:bg-dark-700 text-sm"
                            />
                            <span className="text-gray-500">-</span>
                            <input
                              type="number"
                              placeholder="Max"
                              value={currentFilters.max_price || ''}
                              onChange={(e) => {
                                dispatch(setFilters({ max_price: e.target.value ? Number(e.target.value) : undefined }));
                                setTimeout(() => dispatch(filterProducts() as any), 300);
                              }}
                              className="w-full px-3 py-2 border border-gray-300 dark:border-dark-600 rounded-lg bg-white dark:bg-dark-700 text-sm"
                            />
                          </div>
                          <div className="space-y-2">
                            {[
                              { label: 'Under $25', min: 0, max: 25 },
                              { label: '$25 to $50', min: 25, max: 50 },
                              { label: '$50 to $100', min: 50, max: 100 },
                              { label: '$100+', min: 100, max: undefined },
                            ].map((range) => (
                              <button
                                key={range.label}
                                onClick={() => {
                                  dispatch(setFilters({ min_price: range.min, max_price: range.max }));
                                  setTimeout(() => dispatch(filterProducts() as any), 100);
                                }}
                                className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-dark-700 rounded"
                              >
                                {range.label}
                              </button>
                            ))}
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Clear Filters */}
                {hasActiveFilters && (
                  <Button variant="ghost" size="md" onClick={handleClearFilters}>
                    <XMarkIcon className="h-4 w-4 mr-1" />
                    Clear
                  </Button>
                )}

                {/* Spacer */}
                <div className="flex-1" />

                {/* View Mode Toggle */}
                <div className="hidden sm:flex items-center gap-1 bg-gray-100 dark:bg-dark-700 rounded-lg p-1">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={cn(
                      'p-2 rounded-md transition-colors',
                      viewMode === 'grid'
                        ? 'bg-white dark:bg-dark-600 text-primary-600 dark:text-primary-400 shadow-sm'
                        : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                    )}
                  >
                    <Squares2X2Icon className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={cn(
                      'p-2 rounded-md transition-colors',
                      viewMode === 'list'
                        ? 'bg-white dark:bg-dark-600 text-primary-600 dark:text-primary-400 shadow-sm'
                        : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                    )}
                  >
                    <ListBulletIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>

              {/* Applied Filters */}
              {hasActiveFilters && (
                <div className="flex flex-wrap items-center gap-2 mt-4 pt-4 border-t border-gray-200 dark:border-dark-700">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Active filters:</span>
                  {product_types.map((type) => (
                    <Badge key={type} variant="primary" className="cursor-pointer">
                      {type === 'brand_new' ? 'Brand New' : 'Recycled'}
                      <XMarkIcon
                        className="h-4 w-4 ml-1"
                        onClick={() => {
                          const newTypes = product_types.filter((t) => t !== type);
                          dispatch(setFilters({ product_types: newTypes }));
                          setTimeout(() => dispatch(filterProducts() as any), 100);
                        }}
                      />
                    </Badge>
                  ))}
                  {category_ids.map((catId) => {
                    const cat = categories.find((c) => c.id === catId);
                    return cat ? (
                      <Badge key={catId} variant="primary" className="cursor-pointer">
                        {cat.name}
                        <XMarkIcon
                          className="h-4 w-4 ml-1"
                          onClick={() => {
                            const newCats = category_ids.filter((id) => id !== catId);
                            dispatch(setFilters({ category_ids: newCats }));
                            setTimeout(() => dispatch(filterProducts() as any), 100);
                          }}
                        />
                      </Badge>
                    ) : null;
                  })}
                  <Button variant="ghost" size="sm" onClick={handleClearFilters}>
                    Clear all
                  </Button>
                </div>
              )}

              {/* Results Count */}
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200 dark:border-dark-700">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  <span className="font-semibold text-gray-900 dark:text-white">
                    {displayProducts.length}
                  </span>{' '}
                  results
                </p>
              </div>
            </div>

            {/* Products Grid/List */}
            {isLoadingProducts ? (
              <SkeletonProductGrid count={8} />
            ) : displayProducts.length === 0 ? (
              <div className="text-center py-16 bg-white dark:bg-dark-800 rounded-xl border border-gray-200 dark:border-dark-700">
                <MagnifyingGlassIcon className="h-24 w-24 mx-auto text-gray-300 dark:text-gray-600 mb-4" />
                <h3 className="text-2xl font-semibold text-gray-900 dark:text-white mb-2">
                  No products found
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-md mx-auto">
                  {hasActiveFilters
                    ? 'Try adjusting your filters or search query'
                    : 'Check back later for new products'}
                </p>
                {hasActiveFilters && (
                  <Button onClick={handleClearFilters}>Clear All Filters</Button>
                )}
              </div>
            ) : (
              <>
                <div
                  className={
                    viewMode === 'grid'
                      ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4'
                      : 'space-y-4'
                  }
                >
                  {displayProducts.map((product) => (
                    <ProductCard key={product.id} product={product} viewMode={viewMode} showAddToCart={true} />
                  ))}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="mt-8">
                    <Pagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      onPageChange={handlePageChange}
                    />
                  </div>
                )}
              </>
            )}
        </div>
      </div>

      {/* Mobile Sidebar */}
      <AnimatePresence>
        {showFilters && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="lg:hidden fixed inset-0 bg-black/50 z-40"
              onClick={() => setShowFilters(false)}
            />
            <motion.div
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'spring', damping: 30, stiffness: 300 }}
              className="lg:hidden fixed left-0 top-0 bottom-0 w-80 bg-white dark:bg-dark-850 z-50 overflow-y-auto"
            >
              <div className="sticky top-0 bg-white dark:bg-dark-850 border-b border-gray-200 dark:border-dark-700 p-4 flex items-center justify-between z-10">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Filters</h2>
                <button
                  onClick={() => setShowFilters(false)}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-dark-800 rounded-lg transition-colors"
                >
                  <XMarkIcon className="h-6 w-6 text-gray-600 dark:text-gray-400" />
                </button>
              </div>
              <ProductSidebar onFilterChange={() => {}} />
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Location Modal */}
      <LocationSelectionModal
        isOpen={isLocationModalOpen}
        onClose={() => setIsLocationModalOpen(false)}
        selectedLocations={selectedLocations.map((loc) => (loc.includes(':') ? loc : `0:${loc}`))}
        onSelectLocations={handleLocationModalSelect}
      />
    </div>
  );
};

export default ProductsPageSimple;
